import type { IconName } from '@glplus/glpweb-dsl';

/**
 * The single source of truth for the Vendor Admin tool catalog. The header
 * top-nav, the left "More tools" rail, and the dashboard cards all derive from
 * this list so they never drift out of sync.
 *
 * A `to` is present iff the tool has a real route; `status: 'soon'` tools are
 * roadmap stubs with no route yet.
 */
export type ToolStatus = 'live' | 'soon';
export type SidebarSection = 'vendors';

export interface AdminTool {
  key: string;
  /** Card title (and sidebar label, unless `sidebarLabel` overrides). */
  label: string;
  /** Shorter label for the header top-nav (when `inTopNav`). */
  navLabel?: string;
  /** Shorter label for the left rail (falls back to `label`). */
  sidebarLabel?: string;
  /** One-line description for the dashboard card. */
  description: string;
  /** Route path — present only for live tools. */
  to?: string;
  /** Capability token shown on the card + gating the live dashboard card. */
  capability: string;
  icon: IconName;
  status: ToolStatus;
  /** Appears in the header top-nav (the primary, always-on tools). */
  inTopNav?: boolean;
  /** Section of the left "More tools" rail this belongs to. */
  sidebarSection?: SidebarSection;
  /** Show as a dashboard card. Defaults true; set false for sidebar-only tools. */
  onDashboard?: boolean;
}

export const ADMIN_TOOLS: AdminTool[] = [
  {
    key: 'vendor-products',
    label: 'New products',
    navLabel: 'New Products',
    description:
      'Approve each new product a vendor adds before shoppers see it. A matched product shows what the vendor sent beside ours.',
    to: '/vendor-products',
    capability: 'vendor:read',
    icon: 'listAlt',
    status: 'live',
    inTopNav: true,
    sidebarSection: 'vendors',
  },
  {
    key: 'vendor-applications',
    label: 'Vendor applications',
    navLabel: 'Applications',
    description:
      'Review vendor applications, walk the onboarding pipeline, and approve them onto the marketplace.',
    to: '/vendor-applications',
    capability: 'vendor:read',
    icon: 'shop',
    status: 'live',
    inTopNav: true,
    sidebarSection: 'vendors',
  },
  {
    key: 'vendor-review',
    label: 'Vendor review',
    navLabel: 'Review',
    description: 'Check what vendors send before shoppers see it: products to identify, change requests, locations.',
    to: '/vendor-review',
    capability: 'vendor:read',
    icon: 'listAlt',
    status: 'live',
    inTopNav: true,
    sidebarSection: 'vendors',
  },
  {
    key: 'manage-vendors',
    label: 'Manage vendors',
    navLabel: 'Vendors',
    description:
      'Edit profiles, adjust setup status, and suspend or reactivate approved marketplace vendors.',
    to: '/manage-vendors',
    capability: 'vendor:read',
    icon: 'shop',
    status: 'live',
    inTopNav: true,
    sidebarSection: 'vendors',
  },
  {
    key: 'vendor-settlements',
    label: 'Settlement review',
    navLabel: 'Settlements',
    description: 'Review missing partner discounts, payout adjustments, evidence, and vendor appeals.',
    to: '/vendor-settlements',
    capability: 'vendor:read',
    icon: 'attachMoney',
    status: 'live',
    inTopNav: true,
    sidebarSection: 'vendors',
  },
];

/** Tools shown in the header top-nav (in order). */
export const TOP_NAV_TOOLS = ADMIN_TOOLS.filter((t) => t.inTopNav);

/** Sidebar sections, in display order, with their member tools. */
export const SIDEBAR_SECTIONS: { key: SidebarSection; label: string; tools: AdminTool[] }[] = [
  { key: 'vendors', label: 'Vendors', tools: ADMIN_TOOLS.filter((t) => t.sidebarSection === 'vendors') },
];

/** Live tools that render as dashboard cards under "Your tools". */
export const DASHBOARD_LIVE_TOOLS = ADMIN_TOOLS.filter(
  (t) => t.status === 'live' && t.onDashboard !== false,
);

/** Roadmap tools that render as greyed "Soon" dashboard cards. */
export const DASHBOARD_ROADMAP_TOOLS = ADMIN_TOOLS.filter(
  (t) => t.status === 'soon' && t.onDashboard !== false,
);
